import { useState } from "react";
import { Check, Clock, X, Plus } from "lucide-react";
import { toast } from "sonner";

export const MAX_FOCUS = 3;

// Splits a proposed industry list into "hunt first" (≤3) and "later".
export const splitFocus = (all = []) => ({ industries: all.slice(0, MAX_FOCUS), parkedIndustries: all.slice(MAX_FOCUS) });

const Chip = ({ label, active, onToggle, onRemove, testId }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs pl-2.5 pr-1.5 py-1 rounded-full border transition-colors ${active ? "bg-ism-teal/15 border-ism-teal/40 text-ism-teal" : "bg-white/[0.04] border-white/10 text-gray-400 hover:border-white/25"}`} data-testid={testId}>
    <button onClick={onToggle} className="inline-flex items-center gap-1.5" title={active ? "Click to park for later" : "Click to hunt first"} data-testid={`${testId}-toggle`}>
      {active ? <Check className="h-3 w-3" /> : <Clock className="h-3 w-3" />}{label}
      {!active && <span className="text-[9px] uppercase tracking-wider text-gray-500">later</span>}
    </button>
    <button onClick={onRemove} className="opacity-50 hover:opacity-100" aria-label={`Remove ${label}`} data-testid={`${testId}-remove`}><X className="h-3 w-3" /></button>
  </span>
);

export const IndustryFocusPicker = ({ industries = [], parkedIndustries = [], onChange, testId = "industry-focus" }) => {
  const [draft, setDraft] = useState("");
  const set = (next) => onChange({ industries: next.industries ?? industries, parkedIndustries: next.parkedIndustries ?? parkedIndustries });

  const activate = (v) => {
    if (industries.length >= MAX_FOCUS) return toast.info(`Hunt up to ${MAX_FOCUS} industries first — park one to make room`);
    set({ industries: [...industries, v], parkedIndustries: parkedIndustries.filter((x) => x !== v) });
  };
  const park = (v) => {
    if (industries.length <= 1) return toast.info("Keep at least one industry to hunt first");
    set({ industries: industries.filter((x) => x !== v), parkedIndustries: [v, ...parkedIndustries] });
  };
  const remove = (v) => set({ industries: industries.filter((x) => x !== v), parkedIndustries: parkedIndustries.filter((x) => x !== v) });
  const add = () => {
    const v = draft.trim();
    setDraft("");
    if (!v || [...industries, ...parkedIndustries].some((x) => x.toLowerCase() === v.toLowerCase())) return;
    if (industries.length < MAX_FOCUS) set({ industries: [...industries, v] });
    else { set({ parkedIndustries: [...parkedIndustries, v] }); toast.info(`${v} parked for later — you already hunt ${MAX_FOCUS} industries first`); }
  };

  return (
    <div data-testid={testId}>
      <p className="text-xs text-gray-500 mb-2" data-testid={`${testId}-summary`}>
        Hunting first: <span className="text-ism-teal">{industries.length} of {MAX_FOCUS}</span>
        {parkedIndustries.length > 0 && <> · <span className="text-gray-400">{parkedIndustries.length} parked for later</span></>}
        <span className="text-gray-600"> — sharper targeting finds better buyers; parked segments come back when results run thin.</span>
      </p>
      <div className="flex flex-wrap gap-2 p-2 rounded-xl bg-white/5 border border-white/10 focus-within:border-ism-teal/50 transition-colors min-h-[48px]">
        {industries.map((v) => <Chip key={v} label={v} active onToggle={() => park(v)} onRemove={() => remove(v)} testId={`${testId}-active-${v.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />)}
        {parkedIndustries.map((v) => <Chip key={v} label={v} active={false} onToggle={() => activate(v)} onRemove={() => remove(v)} testId={`${testId}-parked-${v.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />)}
        <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }} onBlur={add}
          placeholder={industries.length || parkedIndustries.length ? "" : "e.g. regional banks, insurers"} className="flex-1 min-w-[140px] bg-transparent text-sm text-white placeholder:text-gray-600 outline-none px-1" data-testid={`${testId}-input`} />
        {draft && <button onClick={add} className="text-ism-teal"><Plus className="h-4 w-4" /></button>}
      </div>
    </div>
  );
};
